/* Shared logic for deriving delivery-condition sale status
   (goodDelivery / protection / pas / partialProtection) from per-pallet
   status instead of a manual per-line dropdown. Kept in its own file
   (no UI), same convention as fobPricesCalc.js / profitCalc.js.

   2026-09-12 — Arturo: protection info usually comes from the client
   about a specific PO (invoice), so the pallet is the source of truth
   and everything above it (invoice line, bill, shipment) is computed
   live from whatever pallets currently say — never a frozen snapshot.

   dumped / donated / samples / transfer are NOT part of this cascade —
   those are manual disposition decisions about a whole line (not a
   delivery-condition grade), so they stay as an explicit line-level
   override that always wins over whatever the pallets compute to. See
   DISPOSITION_STATUSES below. */

/* The 3 delivery-condition values a pallet can carry, all client-
   informed. Unset/undefined on a pallet means "not yet confirmed by
   anyone" — see resolvePendingStage below for what displays instead. */
export var DELIVERY_STATUSES = ["goodDelivery", "protection", "pas"];

/* Whole-line manual dispositions — set directly on the line, never
   derived from pallets, and always takes precedence over the computed
   delivery-condition value when present. */
export var DISPOSITION_STATUSES = ["dumped", "donated", "samples", "transfer"];

/* "Severe" tier for the mixing rule below. PAS and Protection are
   practically the same for this purpose (Arturo, 2026-09-12) — a pallet
   flagged either way counts as severe; a line with all-severe pallets
   keeps whichever single value they actually share (or falls back to
   "protection" if it's a mix of the two, since that's the more
   conservative label). */
var SEVERE = { protection: true, pas: true };

/* rows: array of { saleStatus } (or any object carrying that field) —
   e.g. pallet rows from invoice.palletAllocations for a line, or invoice
   LINES (whose saleStatus may already be a computed "partialProtection"
   from a lower level) flattened across several invoices for a bill/
   shipment.

   2026-09-12 — Arturo: "el cliente informa todos los status" — a pallet
   with no saleStatus set has NOT been confirmed good by anyone, it's
   just unreported. Treating unset as "goodDelivery" (the old behavior)
   meant every pallet showed Good Delivery the moment it was allocated
   to an invoice, even for shipments the client hasn't received yet —
   caught live on INV-0140..INV-0143 (lots 58047/58048), still "sent",
   zero pallets ever touched, all showing Good Delivery regardless.
   Unset now means "pending" (returns null → callers show "—"), UNLESS
   a real problem (protection/pas) was already reported on another
   pallet in the same group — that's real information and must surface
   immediately, it shouldn't wait on the rest of the pallets to be
   confirmed. Only when EVERY row in the group has been explicitly
   confirmed "goodDelivery" does the group itself read as good.
   Returns null when there are no rows, or nothing has been confirmed
   yet — callers should show a blank/pending status in that case, never
   assume good. */
export function deriveDeliveryStatus(rows) {
  var list = (rows || []).filter(function (r) { return r; });
  if (list.length === 0) return null;

  var sawGood = false;
  var sawPending = false;
  var nonGoodValues = {};
  list.forEach(function (r) {
    var st = r.saleStatus;
    /* 2026-09-14 — Arturo can now explicitly pin a pallet to
       "pendingPickUp"/"pendingDelivery" (see PENDING_STATUSES below;
       previously these were NEVER a stored value, only an on-the-fly
       fallback via resolvePendingStage). An explicitly-pinned pending
       value must aggregate exactly like an unset one — it's still
       "nothing confirmed yet", not a real delivery-condition grade —
       otherwise pinning ONE pallet to Pending Delivery while its
       siblings stay unset flips the whole line to "Partial Protection"
       (caught live testing: real bug, a pending pallet is not a
       protection problem). */
    if (!st || PENDING_STATUSES.indexOf(st) !== -1) { sawPending = true; return; }
    if (st === "goodDelivery") sawGood = true;
    else nonGoodValues[st] = true;
  });

  var nonGoodKeys = Object.keys(nonGoodValues);
  if (nonGoodKeys.length === 0) {
    if (sawPending) return null; // nothing reported yet, or not everyone confirmed
    return "goodDelivery"; // every row explicitly confirmed good
  }
  if (!sawGood && !sawPending) {
    if (nonGoodKeys.length === 1) return nonGoodKeys[0];
    if (nonGoodKeys.every(function (k) { return SEVERE[k]; })) return "protection";
  }
  return "partialProtection";
}

/* A pallet-allocation row {lot, palletNum, cases, saleStatus} carries no
   product info of its own — matching it to "which invoice line is this
   pallet's cases part of" is done by looking up the pallet's real
   product (productBase/category/size) in the shipment's palletManifest
   (InvoiceRegistry.js already has palletMetaFor(lot, palletNum) for
   this) and comparing against each line's own product/category/size +
   lot. This is more robust than a stored line index — it keeps working
   even if lines get reordered or re-added, since it's re-derived from
   real product data every time rather than a position that can drift. */
export function allocationMatchesLine(line, lot, palletMeta) {
  if (!line || !palletMeta) return false;
  if (String(line.lot || "") !== String(lot || "")) return false;
  var lineProduct = String(line.productBase || line.productName || "").trim().toLowerCase();
  var metaProduct = String(palletMeta.productBase || "").trim().toLowerCase();
  if (lineProduct !== metaProduct) return false;
  var lineCat = String(line.category || "").trim().toLowerCase();
  var metaCat = String(palletMeta.category || "").trim().toLowerCase();
  if (lineCat && metaCat && lineCat !== metaCat) return false;
  /* 2026-10-20 — found live on PU 17447/InvoiceRegistry.js's title-bar
     status badge: older invoice lines never carried a `.size` field at
     all, while a pallet's manifest entry always does — the old
     unconditional comparison ("" !== "6oz") silently failed the match
     and threw away a real, already-confirmed "protection" status in
     favor of the pending default. Same lenient style as the sibling
     matchLineToInvoiceLines() below: only a genuine mismatch (BOTH
     sides have a value and they differ) disqualifies the match, never
     one side just being blank. */
  var lineSize = String(line.size || "").trim().toLowerCase();
  var metaSize = String(palletMeta.size || "").trim().toLowerCase();
  if (lineSize && metaSize && lineSize !== metaSize) return false;
  return true;
}

/* 2026-09-12 — Arturo wants the PER-PALLET status visible on Bill and
   Shipment too, not just the rolled-up line status. A single physical
   pallet (lot+palletNum) can appear in more than one invoice's
   palletAllocations if its cases got split across invoices — same
   mixing rule as deriveDeliveryStatus applies across those rows.
   allInvoices: the full invoices array (bill/shipment don't have their
   own pallet-level status, only the invoices that drew cases from that
   pallet do). Returns null when no invoice has allocated this pallet
   yet, so callers can show "—" instead of guessing goodDelivery. */
export function derivePalletStatus(lot, palletNum, allInvoices) {
  var rows = [];
  (allInvoices || []).forEach(function (inv) {
    (inv.palletAllocations || []).forEach(function (a) {
      if (a && String(a.lot) === String(lot) && String(a.palletNum) === String(palletNum)) rows.push(a);
    });
  });
  return deriveDeliveryStatus(rows);
}

/* Pre-delivery lifecycle stages (2026-09-12, Arturo) — shown ONLY as a
   display-time default while deriveDeliveryStatus/derivePalletStatus
   return null (nothing confirmed by the client yet). Never written to
   saleStatus, never offered as a pickable option anywhere — always
   computed fresh, so it self-updates the instant its trigger fires.

   "pendingPickUp" is the universal default for any unconfirmed pallet/
   line, on every client. "pendingDelivery" only ever applies to Four
   Seasons (SweetLeaf) invoices, and only once that invoice's SweetLeaf
   mirror has sent its Passing/BOL (passingSentAt set) — "cuando se
   agrega el Passing a la factura de sweetleaf entonces cambia
   automatico a Pending Delivery". Every other client stays on
   Pending Pick Up until someone manually sets the real status on the
   Invoice ("los demas otros clientes se ejecutan manualmente" — no
   automatic second stage for them, there's no Passing-equivalent event
   to key off of). */
export var PENDING_STATUSES = ["pendingPickUp", "pendingDelivery"];

/* slInvoices: the raw sweetleaf_invoices array. Returns a lookup map
   keyed by the linked GFI invoice's id (SL invoices carry gfiInvoiceId
   — see InvoiceRegistry.js's "Copy to SL", which sets it to fresh.id). */
export function buildSlPassingIndex(slInvoices) {
  var idx = {};
  (slInvoices || []).forEach(function (s) {
    if (s && s.gfiInvoiceId) idx[String(s.gfiInvoiceId)] = s;
  });
  return idx;
}

/* contributingInvoices: the GFI invoice(s) whose pallets/lines feed the
   status being displayed (just [invoice] when rendering that invoice's
   own form; every invoice that allocated a given lot+pallet, or every
   invoice on a lot, when rendering Bill/Shipment). slPassingIndex: from
   buildSlPassingIndex(). Four Seasons wins the moment ANY contributing
   invoice's mirror has sent its Passing — a bill/shipment line spanning
   several POs should flip as soon as one of them is truly in transit. */
export function resolvePendingStage(contributingInvoices, slPassingIndex) {
  var anyPassingSent = (contributingInvoices || []).some(function (inv) {
    if (!inv || !inv.slImported) return false;
    var mirror = slPassingIndex && slPassingIndex[String(inv.id)];
    return !!(mirror && mirror.passingSentAt);
  });
  return anyPassingSent ? "pendingDelivery" : "pendingPickUp";
}

/* Same contributing-invoices lookup derivePalletStatus uses internally,
   exposed so callers can feed it into resolvePendingStage. */
export function invoicesForPallet(lot, palletNum, allInvoices) {
  return (allInvoices || []).filter(function (inv) {
    return (inv.palletAllocations || []).some(function (a) {
      return a && String(a.lot) === String(lot) && String(a.palletNum) === String(palletNum);
    });
  });
}

/* Matches a Bill (or any lot-level) line to the invoice lines that feed
   it — same product/category/size match used by the Invoice→Bill push
   in InvoiceRegistry.js/BillRegistry.js's handleSave. Exposed so DISPLAY
   code can recompute a line's live status the same way, instead of
   trusting whatever got persisted at the last save. */
export function matchLineToInvoiceLines(line, invoiceLinesForLot) {
  if (!line) return [];
  var key = String(line.productName || line.productBase || "").trim().toLowerCase();
  var cat = String(line.category || "").trim().toLowerCase();
  var size = String(line.size || "").trim().toLowerCase();
  return (invoiceLinesForLot || []).filter(function (il) {
    var k = String(il.productName || il.productBase || "").trim().toLowerCase();
    if (!k || k !== key) return false;
    if (cat && String(il.category || "").trim().toLowerCase() !== cat) return false;
    if (size && String(il.size || "").trim().toLowerCase() !== size) return false;
    return true;
  });
}

/* 2026-09-16 — Arturo: "no debe volver a suceder porque nunca me
   informan al mismo tiempo" — two POs on the same lot routinely get
   their real delivery status reported by the client days apart, so a
   Bill line's PERSISTED saleStatus (only refreshed by the Invoice→Bill
   push at save time, or the Bill's own defensive re-sync on its own
   save) can legitimately drift stale between those two events and stay
   wrong until someone happens to re-save the Bill. Display code should
   never trust that persisted value on its own — recompute it live from
   whatever the real linked invoices currently say, the same "always
   computed, never a frozen snapshot" rule the rest of this file already
   follows for pallets. DISPOSITION_STATUSES (dumped/donated/samples/
   transfer) are a manual whole-line override and always win as-is.
   Falls back to the line's own stored value only when no matching
   invoice lines are found at all (e.g. a legacy bill with no linked
   invoices) — never silently blanks out a real status. */
/* Twin River pallet status (2026-10-15) — Arturo: "las de twin
   river, el mismo reporte de calidad que me envian deriva la carga a
   proteccion a nivel pallet desde el mismo embarque porque ellos
   reciben la fruta directamente en su bodega y llevan a cabo la
   inspeccion de calidad y ahi mismo me informan los pallets que estan
   protegidos" — a deliberate exception to REGLA DE STATUS DE PALLET's
   "only the Invoice can set pallet status" rule, scoped ONLY to
   ClariFresh-mode shipments (Twin River). Four Seasons/everyone else
   is untouched — still Invoice-only, exactly as before.

   shipment.palletManifest[i].twinRiverStatus is a manual override
   (tri-state SearchSelect: "" unreported / "goodDelivery" /
   "protection") Arturo can still set by hand for an edge case, but it
   is no longer the only way this resolves — 2026-10-15, 2nd round,
   Arturo: "los de twin river nunca estaban pending pick up porque los
   reciben ellos mismos... despues de capturarlos deben pasar al
   status de recepcion cuando me llega el reporte que seria
   proteccion, good delivery o lo que sea" — Twin River's own report
   already grades each pallet (texasQuality/texasCondition/
   texasSevereCondition, the same tiers Arturo already transcribes
   into the Texas QC Report columns for every pallet), so capturing
   THAT should be what resolves the real status, without needing a
   second, separate manual pick. Per Twin River's own spec
   (TR-SPEC-BLK-MX/BB-MX): only Grade 1 (Very Good) and Grade 2
   (Good) pass; Fair/Poor/Bad all fail — mirrors TEXAS_QC_COLORS'
   existing green/purple-vs-yellow/red split in ShipmentRegistry.js,
   duplicated here as TWIN_RIVER_PASS_TIERS per this file's own "no
   UI, no cross-file imports of UI constants" convention. */
var TWIN_RIVER_PASS_TIERS = { excellent: true, veryGood: true, goodQuality: true };
function twinRiverGradeStatus(p) {
  if (!p) return null;
  if (p.twinRiverStatus) return p.twinRiverStatus;
  var tiers = [p.texasQuality, p.texasCondition, p.texasSevereCondition].filter(function (t) { return t; });
  if (tiers.length === 0) return null;
  return tiers.every(function (t) { return TWIN_RIVER_PASS_TIERS[t]; }) ? "goodDelivery" : "protection";
}

/* Always read live from the shipment, never synced/pushed anywhere —
   same "no frozen snapshot" rule as the rest of this file. Returns
   null only when this isn't a ClariFresh shipment at all, or the
   pallet doesn't exist — for any real Twin River pallet this always
   resolves to something real ("pendingDelivery" while ungraded,
   reusing the existing pending-stage value rather than inventing a
   new one just for this — "ya está en su bodega, falta el reporte"
   fits that label well enough, and it's never confused with
   Pending Pick Up, which is the one thing Arturo explicitly does not
   want shown here). */
export function twinRiverPalletStatus(shipment, palletNum) {
  if (!shipment || shipment.texasQcMode !== "clarifresh") return null;
  var p = (shipment.palletManifest || []).find(function (pp) { return String(pp.palletNum || "") === String(palletNum || ""); });
  if (!p) return null;
  return twinRiverGradeStatus(p) || "pendingDelivery";
}

/* Whole-lot rollup for Twin River — same mixing semantics as every
   other aggregate in this file (deriveDeliveryStatus over a synthetic
   {saleStatus} row per real pallet, graded via twinRiverGradeStatus
   instead of invoice.palletAllocations). Returns null for a
   non-ClariFresh shipment (caller falls back to the normal
   invoice-based computation) or when the shipment has no real
   pallets yet; otherwise never pendingPickUp, same reasoning as
   twinRiverPalletStatus above. */
export function twinRiverLotStatus(shipment) {
  if (!shipment || shipment.texasQcMode !== "clarifresh") return null;
  var realPallets = (shipment.palletManifest || []).filter(function (p) { return p && (p.palletNum || p.productBase); });
  if (realPallets.length === 0) return null;
  var rows = realPallets.map(function (p) { return { saleStatus: twinRiverGradeStatus(p) || "" }; });
  return deriveDeliveryStatus(rows) || "pendingDelivery";
}

/* Per-product rollup for Twin River (2026-10-30) — Arturo, PU 17475/lot
   58074: "las lineas de producto de ese invoice me marcan la zarzamora
   partial protecion y el arandano partial protection, las protecciones
   se definen por producto... si los pallets de zarzamora estan
   protegidos todos en el manifiesto, la linea debe ser protection no
   partial protection... no debe mezclar productos". Every caller of
   twinRiverLotStatus() above applied that single whole-LOT value to
   EVERY product line uniformly — a mixed-grade lot (zarzamora fully
   protected, arándano fully good) collapsed both lines to the same
   "partialProtection" blend instead of each product keeping its own
   real status. This mirrors twinRiverLotStatus exactly but only rolls
   up the pallets whose productBase matches (case/whitespace-insensitive,
   same normalization as matchLineToInvoiceLines elsewhere in this
   file) — a product with pallets that are ALL protection still
   correctly resolves to "protection" (not partial); partial only
   happens when pallets of THAT SAME product are themselves mixed. */
export function twinRiverProductStatus(shipment, productBase) {
  if (!shipment || shipment.texasQcMode !== "clarifresh") return null;
  var key = String(productBase || "").trim().toLowerCase();
  if (!key) return null;
  var matchingPallets = (shipment.palletManifest || []).filter(function (p) {
    return p && String(p.productBase || "").trim().toLowerCase() === key;
  });
  if (matchingPallets.length === 0) return null;
  var rows = matchingPallets.map(function (p) { return { saleStatus: twinRiverGradeStatus(p) || "" }; });
  return deriveDeliveryStatus(rows) || "pendingDelivery";
}

export function deriveBillLineLiveStatus(line, invoiceLinesForLot) {
  if (!line) return null;
  if (DISPOSITION_STATUSES.indexOf(line.saleStatus) !== -1) return line.saleStatus;
  var matched = matchLineToInvoiceLines(line, invoiceLinesForLot);
  if (matched.length === 0) return line.saleStatus || null;
  return deriveDeliveryStatus(matched) || line.saleStatus || null;
}
